import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { useSearch } from "@/context/SearchContext";
import { toast } from "sonner";
import { SeverityBadge } from "@/components/SeverityBadge";
import AiAnalysisPanel from "@/components/AiAnalysisPanel";
import ThreatDrawer from "@/components/ThreatDrawer";
import { downloadCsv, exportThreatsPdf } from "@/lib/export";
import {
  ShieldWarning, Warning, HardDrives, Timer, TrendUp,
  FileCsv, FilePdf, CaretLeft, CaretRight,
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid,
} from "recharts";

const PAGE_SIZE = 8;

function StatCard({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-[#0C0C14] border border-[#1F1F33] rounded-none p-5 hover:border-[#33334D] transition-colors duration-200 fade-up"
    >
      <div className="flex items-start justify-between">
        <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">{label}</span>
        <Icon size={18} weight="duotone" color={accent} />
      </div>
      <div className="font-head text-4xl font-black mt-3 tracking-tighter" style={{ color: accent }}>
        {value}
      </div>
      {sub && <div className="text-[11px] text-[#71717A] mt-1">{sub}</div>}
    </div>
  );
}

export default function Overview() {
  const [stats, setStats] = useState(null);
  const [threats, setThreats] = useState([]);
  const [timeline, setTimeline] = useState([]);
  const [selected, setSelected] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [wsLive, setWsLive] = useState(false);
  const { query } = useSearch();
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const [s, t, a] = await Promise.all([
      api.get("/dashboard/stats"),
      api.get("/threats"),
      api.get("/analytics"),
    ]);
    setStats(s.data);
    setThreats(t.data);
    setTimeline(a.data.timeline);
    setSelected((cur) => cur || t.data[0] || null);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Live WebSocket feed — new threats stream in in real time.
  useEffect(() => {
    const wsUrl = API.replace(/^http/, "ws") + "/ws/threats";
    let ws;
    let closed = false;
    try {
      ws = new WebSocket(wsUrl);
    } catch (e) {
      return;
    }
    ws.onopen = () => setWsLive(true);
    ws.onclose = () => { if (!closed) setWsLive(false); };
    ws.onerror = () => setWsLive(false);
    ws.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        if (msg.type === "threat" && msg.threat) {
          setThreats((prev) => [msg.threat, ...prev].slice(0, 80));
          setStats((prev) => prev ? {
            ...prev,
            active_threats: prev.active_threats + 1,
            critical_threats: prev.critical_threats + (msg.threat.severity === "critical" ? 1 : 0),
          } : prev);
          toast(`New ${msg.threat.severity.toUpperCase()} threat`, {
            description: `${msg.threat.title} · ${msg.threat.source_ip} → ${msg.threat.target}`,
          });
        }
      } catch (e) {}
    };
    return () => { closed = true; ws && ws.close(); };
  }, []);

  const q = query.trim().toLowerCase();
  const filteredThreats = q
    ? threats.filter((t) =>
        [t.title, t.category, t.source_ip, t.target, t.severity, t.status]
          .join(" ").toLowerCase().includes(q))
    : threats;

  useEffect(() => { setPage(1); }, [q]);

  const totalPages = Math.max(1, Math.ceil(filteredThreats.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageThreats = filteredThreats.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const openThreat = (t) => { setSelected(t); setDrawerOpen(true); };

  const escalate = (t) => {
    navigate("/incidents", { state: { fromThreat: t } });
  };

  const onAnalyzed = (id, analysis) => {
    setThreats((prev) => prev.map((x) => (x.id === id ? { ...x, ai_analysis: analysis } : x)));
    setSelected((cur) => (cur && cur.id === id ? { ...cur, ai_analysis: analysis } : cur));
  };

  const exportCsv = async () => {
    try { await downloadCsv("/export/threats", "threats.csv"); toast.success("CSV exported"); }
    catch (e) { toast.error("Export failed"); }
  };

  return (
    <div data-testid="overview-page">
      <div className="mb-6">
        <p className="text-[10px] uppercase tracking-[0.3em] text-[#00F0FF]">Command Console</p>
        <h1 className="font-head text-3xl tracking-tighter font-black mt-1">Operations Overview</h1>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <StatCard testid="stat-active-threats" icon={ShieldWarning} label="Active Threats"
          value={stats?.active_threats ?? "—"} sub={`${stats?.critical_threats ?? 0} critical`} accent="#FF3B30" />
        <StatCard testid="stat-open-incidents" icon={Warning} label="Open Incidents"
          value={stats?.open_incidents ?? "—"} sub="Awaiting response" accent="#FF9500" />
        <StatCard testid="stat-monitored-assets" icon={HardDrives} label="Monitored Assets"
          value={stats?.monitored_assets ?? "—"} sub={`${stats?.at_risk_assets ?? 0} at risk`} accent="#00F0FF" />
        <StatCard testid="stat-response-time" icon={Timer} label="Avg Response"
          value={`${stats?.avg_response_mins ?? "—"}m`} sub="Mean time to respond" accent="#FFCC00" />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        {/* Left: chart + threat feed */}
        <div className="xl:col-span-2 space-y-6">
          <div className="bg-[#0C0C14] border border-[#1F1F33] rounded-none p-5">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <TrendUp size={16} weight="duotone" color="#00F0FF" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">Threats Detected · 7 Days</span>
              </div>
            </div>
            <div style={{ width: "100%", height: 200 }}>
              <ResponsiveContainer>
                <AreaChart data={timeline} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="thGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#00F0FF" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#00F0FF" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#1F1F33" vertical={false} />
                  <XAxis dataKey="date" stroke="#71717A" fontSize={10} tickLine={false} axisLine={{ stroke: "#1F1F33" }} />
                  <YAxis stroke="#71717A" fontSize={10} tickLine={false} axisLine={false} allowDecimals={false} />
                  <Tooltip
                    contentStyle={{ background: "#12121E", border: "1px solid #1F1F33", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 12 }}
                    labelStyle={{ color: "#fff" }}
                  />
                  <Area type="monotone" dataKey="threats" stroke="#00F0FF" strokeWidth={2} fill="url(#thGrad)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-[#0C0C14] border border-[#1F1F33] rounded-none" data-testid="threat-feed">
            <div className="flex items-center justify-between px-5 py-4 border-b border-[#1F1F33] gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 ${wsLive ? "bg-[#00F0FF] pulse-dot" : "bg-[#71717A]"}`} data-testid="live-indicator" />
                <span className="text-[10px] uppercase tracking-[0.2em] text-[#71717A]">
                  Live Threat Feed {wsLive ? "· LIVE" : "· OFFLINE"}
                </span>
              </div>
              <div className="flex items-center gap-3">
                <span className="text-[10px] text-[#71717A]">
                  {q ? `${filteredThreats.length}/${threats.length}` : threats.length} events
                </span>
                <button data-testid="export-threats-csv" onClick={exportCsv}
                  className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#71717A] hover:text-[#00F0FF] transition-colors">
                  <FileCsv size={14} /> CSV
                </button>
                <button data-testid="export-threats-pdf" onClick={() => exportThreatsPdf(filteredThreats)}
                  className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-[#71717A] hover:text-[#00F0FF] transition-colors">
                  <FilePdf size={14} /> PDF
                </button>
              </div>
            </div>
            <div className="min-h-[420px]">
              {pageThreats.map((t) => (
                <button
                  key={t.id}
                  data-testid={`threat-row-${t.id}`}
                  onClick={() => openThreat(t)}
                  className={`w-full text-left flex items-center gap-4 px-5 py-3 border-b border-[#1F1F33] border-l-2 transition-all duration-200 ${
                    selected?.id === t.id ? "border-l-[#00F0FF] bg-white/[0.03]" : "border-l-transparent hover:bg-white/[0.02]"
                  }`}
                >
                  <div className="w-24 shrink-0"><SeverityBadge level={t.severity} /></div>
                  <div className="min-w-0 flex-1">
                    <div className="text-sm truncate">{t.title}</div>
                    <div className="text-[11px] text-[#71717A] truncate">
                      {t.source_ip} → {t.target} · {t.category}
                    </div>
                  </div>
                  <span className="text-[10px] uppercase tracking-wider text-[#71717A] shrink-0 hidden sm:block">
                    {t.status}
                  </span>
                </button>
              ))}
              {filteredThreats.length === 0 && (
                <div className="px-5 py-16 text-center text-xs text-[#71717A]">
                  No threats match “{query}”.
                </div>
              )}
            </div>
            {filteredThreats.length > 0 && (
              <div className="flex items-center justify-between px-5 py-3 border-t border-[#1F1F33]" data-testid="threat-pagination">
                <span className="text-[10px] uppercase tracking-wider text-[#71717A]">
                  Page {currentPage} / {totalPages}
                </span>
                <div className="flex items-center gap-2">
                  <button data-testid="prev-page" disabled={currentPage <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    className="p-1.5 border border-[#1F1F33] text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                    <CaretLeft size={14} />
                  </button>
                  <button data-testid="next-page" disabled={currentPage >= totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    className="p-1.5 border border-[#1F1F33] text-[#A1A1AA] hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                    <CaretRight size={14} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Right: AI panel */}
        <div className="xl:col-span-1">
          <div className="sticky top-24">
            <AiAnalysisPanel threat={selected} onAnalyzed={onAnalyzed} />
          </div>
        </div>
      </div>

      <ThreatDrawer
        threat={selected}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        onAnalyzed={onAnalyzed}
        onCreateIncident={(t) => { setDrawerOpen(false); escalate(t); }}
      />
    </div>
  );
}
